import { getIndexedDb, IndexedDb } from "utils/indexedDb";

export interface NetworkMonitorState {
  isOnline: boolean;
  timeBeginOffline: number;
}

const defaultState: NetworkMonitorState = {
  isOnline: undefined as any,
  timeBeginOffline: undefined as any,
};

class NetworkMonitor {
  protected indexedDb?: IndexedDb;
  private state: NetworkMonitorState = defaultState;
  private listeners: Set<() => void> = new Set();

  constructor() {
    this.init();
  }

  getState = (): NetworkMonitorState => {
    return this.state;
  };

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  };

  private setState = (newState: NetworkMonitorState): void => {
    this.state = { ...newState };
    this.listeners.forEach((listener) => listener());
  };

  private async init() {
    try {
      this.indexedDb = await getIndexedDb();
      const networkData = await this.getNetworkData();

      await this.updateStatus(
        networkData.isOnline,
        networkData.timeBeginOffline
      );
    } catch (error) {
      /* eslint-disable-next-line no-console */
      console.error(`Init network monitor error: ${error}`);

      this.setState({ ...this.state, isOnline: true, timeBeginOffline: 0 });
    }
  }

  async updateStatus(isOnline: boolean, _timeBeginOffline?: number) {
    let timeBeginOffline = this.state.timeBeginOffline ?? _timeBeginOffline;
    if (!isOnline && this.state.isOnline) {
      timeBeginOffline = Date.now();
    } else if (isOnline) {
      timeBeginOffline = 0;
    }

    const result = await this.indexedDb?.changeNetworkStatus(
      isOnline,
      timeBeginOffline
    );
    if (result) {
      this.setState({ ...this.state, isOnline, timeBeginOffline });
    }
  }

  private async getNetworkData(): Promise<{
    isOnline: boolean;
    timeBeginOffline: number;
  }> {
    try {
      const networkData = await this.indexedDb?.getNetworkStatusFromIndexedDB();

      return networkData;
    } catch (err) {
      return { isOnline: true, timeBeginOffline: 0 };
    }
  }

  async getStatus(): Promise<boolean> {
    try {
      const networkStatus =
        await this.indexedDb?.getNetworkStatusFromIndexedDB();

      return networkStatus?.isOnline ?? navigator.onLine;
    } catch (err) {
      return navigator.onLine;
    }
  }

  /**
   * IMPORTANT: Use getStatus() method in Service Worker context
   * ----------------------------------------------------------
   * Due to the separate execution contexts between service workers and the main thread:
   *
   * - Service workers maintain their own memory space, separate from the browser's main thread
   * - Any in-memory state (like NetworkMonitor's internal state) is NOT shared between contexts
   * - Synchronous methods that read from memory (e.g., isOnline()) may return stale/incorrect values
   *
   * When in a service worker context, ALWAYS use:
   *   const isOnline = await networkMonitor.getStatus();
   *
   * Instead of:
   *   const isOnline = networkMonitor.isOnline(); // May return incorrect value in service worker
   */
  isOnline() {
    return this.getState().isOnline;
  }
}

const networkMonitor = new NetworkMonitor();

export default networkMonitor;
